import { ref, computed, h, watch, onMounted } from 'vue'
import { SettingOutlined } from '@ant-design/icons-vue'
import { Dropdown, Menu, Checkbox } from 'ant-design-vue'

const STORAGE_KEY = 'settings-table-columns'

export function useTableColumns(initialColumns, options = {}) {
    const allColumns = ref(initialColumns)
    const settingsWidth = options.settingsWidth || 40;
    const tableKey = options.tableKey;

    const visibleColumns = computed(() => allColumns.value.filter(col => col.visible))

    const scrollX = computed(() => {
        const totalWidth = visibleColumns.value.reduce((sum, col) => sum + (col.width || 0), 0)
        return totalWidth + settingsWidth + 50
    })

    const toggleColumn = (col) => {
        const visibleCount = visibleColumns.value.length
        if (col.visible && visibleCount === 1) return
        col.visible = !col.visible
    }

    const isLastVisible = (col) => col.visible && visibleColumns.value.length === 1

    const settingsColumn = computed(() => ({
        title: () => h(
            Dropdown,
            { trigger: ['click'], placement: 'bottomRight', getPopupContainer: () => document.body },
            {
                default: () => h(SettingOutlined, { style: { cursor: 'pointer', color: '#999' } }),
                overlay: () => h(
                    Menu,
                    { style: { maxHeight: '400px', overflowY: 'auto', minWidth: '200px' } },
                    () => allColumns.value.map(col => h(
                        Menu.Item,
                        { key: col.dataIndex, disabled: isLastVisible(col) },
                        () => h(
                            Checkbox,
                            {
                                checked: col.visible,
                                onChange: () => toggleColumn(col),
                                onClick: (e) => e.stopPropagation()
                            },
                            () => col.title
                        )
                    ))
                )
            }
        ),
        dataIndex: 'settings',
        width: settingsWidth,
        align: 'center',
        key: 'settings'
    }))

    const columns = computed(() => [settingsColumn.value, ...visibleColumns.value])

    const resetColumns = () => {
        allColumns.value.forEach(col => col.visible = true)
    }

    onMounted(() => {
        if (!tableKey) return
        try {
            const allSaved = localStorage.getItem(STORAGE_KEY)
            if (allSaved) {
                const parsed = JSON.parse(allSaved)
                const savedMap = parsed[tableKey]
                if (savedMap && Array.isArray(savedMap)) {
                    allColumns.value.forEach(col => {
                        const s = savedMap.find(c => c.dataIndex === col.dataIndex)
                        if (s !== undefined) col.visible = s.visible
                    })
                }
            }
        } catch (e) {
            console.warn(`Load columns error: ${tableKey}`, e)
        }
    })

    if (tableKey) {
        watch(
            () => allColumns.value.map(c => ({ dataIndex: c.dataIndex, visible: c.visible })),
            (newVal) => {
                try {
                    const allSaved = localStorage.getItem(STORAGE_KEY)
                    const parsed = allSaved ? JSON.parse(allSaved) : {}
                    parsed[tableKey] = newVal
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed))
                } catch (e) {
                    console.warn(`Save columns error: ${tableKey}`, e)
                }
            },
            { deep: true }
        )
    }

    return {
        columns,
        scrollX,
        allColumns,
        toggleColumn,
        resetColumns,
        visibleColumns
    }
}